import { useState, useMemo, useEffect } from "react";

import { Card, CardContent } from "@components/ui/Card";
import {
  getWeekNumber,
  getMonthWeekRange,
  formatWeekRange,
  getDatesWithFlights,
  dateHasFlights,
  getWeekStart,
} from "../utils";
import MonthNavigation from "./MonthNavigation";
import WeekNavigation from "./WeekNavigation";
import CalendarHeader from "./CalendarHeader";

const weekDays = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

const CalendarView = ({ flights = [], currentMonth, onDateRangeChange }) => {
  const [viewMode, setViewMode] = useState("month");
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    if (currentMonth) return currentMonth;
    return new Date();
  });
  const [viewWeekStart, setViewWeekStart] = useState(() => {
    const today = new Date();
    const initialMonth = currentMonth || today;
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    const initialMonthValue = initialMonth.getMonth();
    const initialYearValue = initialMonth.getFullYear();

    if (todayMonth === initialMonthValue && todayYear === initialYearValue) {
      return getWeekStart(today);
    } else {
      const firstDayOfMonth = new Date(initialYearValue, initialMonthValue, 1);
      return getWeekStart(firstDayOfMonth);
    }
  });

  useEffect(() => {
    if (currentMonth) {
      setViewMonth(currentMonth);
    } else {
      const today = new Date();
      setViewMonth(today);
    }
  }, [currentMonth]);

  const datesWithFlights = useMemo(() => {
    return getDatesWithFlights(flights, viewMonth);
  }, [flights, viewMonth]);

  const firstDayOfMonth = useMemo(() => {
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  }, [viewMonth]);

  const lastDayOfMonth = useMemo(() => {
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  }, [viewMonth]);

  const getMonthRange = (month) => {
    const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    endDate.setHours(23, 59, 59, 999);
    return { viewMode: "month", startDate, endDate };
  };

  const getWeekRange = (weekStart) => {
    const startDate = new Date(weekStart);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);
    return { viewMode: "week", startDate, endDate };
  };

  const getDayRange = (date) => {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);
    return { viewMode: "day", startDate, endDate, selectedDate: date };
  };

  useEffect(() => {
    onDateRangeChange?.(getMonthRange(viewMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentWeekNumber = useMemo(() => {
    if (viewMode === "week") {
      return getWeekNumber(viewWeekStart);
    }
    return getWeekNumber(viewMonth);
  }, [viewMode, viewWeekStart, viewMonth]);

  const calendarDays = useMemo(() => {
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const adjustedStartingDay =
      startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];

    const prevMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth() - 1,
      1
    );
    const lastDayPrevMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth(),
      0
    ).getDate();

    for (let i = adjustedStartingDay - 1; i >= 0; i--) {
      const day = lastDayPrevMonth - i;
      const date = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), day);
      days.push({
        day,
        date,
        month: "prev",
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
      days.push({
        day,
        date,
        month: "current",
      });
    }

    const totalCells = days.length;
    const remainingCells = 42 - totalCells;

    const nextMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth() + 1,
      1
    );
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), day);
      days.push({
        day,
        date,
        month: "next",
      });
    }

    return days;
  }, [viewMonth, firstDayOfMonth, lastDayOfMonth]);

  const calendarRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      rows.push(calendarDays.slice(i, i + 7));
    }
    return rows;
  }, [calendarDays]);

  const visibleWeekRowIndex = useMemo(() => {
    if (viewMode !== "week") return null;

    const weekStartDate = new Date(viewWeekStart);
    weekStartDate.setHours(0, 0, 0, 0);

    for (let rowIndex = 0; rowIndex < calendarRows.length; rowIndex++) {
      const row = calendarRows[rowIndex];
      for (const dayItem of row) {
        const dayDate = new Date(dayItem.date);
        dayDate.setHours(0, 0, 0, 0);
        if (dayDate.getTime() === weekStartDate.getTime()) {
          return rowIndex;
        }
      }
    }
    return 0;
  }, [viewMode, viewWeekStart, calendarRows]);

  const handleDateClick = (dayItem) => {
    setSelectedDate(dayItem.date);
    if (dayItem.month === "prev" || dayItem.month === "next") {
      const newMonth = new Date(dayItem.date.getFullYear(), dayItem.date.getMonth(), 1);
      setViewMonth(newMonth);
    }
    onDateRangeChange?.(getDayRange(dayItem.date));
  };

  const handleMonthSelect = (month) => {
    setSelectedDate(null);
    setViewMonth(month);
    onDateRangeChange?.(getMonthRange(month));
  };

  const handleViewToggle = () => {
    setSelectedDate(null);
    if (viewMode === "month") {
      const today = new Date();
      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();
      const viewMonthValue = viewMonth.getMonth();
      const viewYearValue = viewMonth.getFullYear();

      let weekStart;
      if (todayMonth === viewMonthValue && todayYear === viewYearValue) {
        weekStart = getWeekStart(today);
      } else {
        const firstDayOfMonth = new Date(viewYearValue, viewMonthValue, 1);
        weekStart = getWeekStart(firstDayOfMonth);
      }

      setViewWeekStart(weekStart);
      setViewMode("week");
      onDateRangeChange?.(getWeekRange(weekStart));
    } else {
      setViewMode("month");
      onDateRangeChange?.(getMonthRange(viewMonth));
    }
  };

  const handleWeekSelect = (weekStart) => {
    setSelectedDate(null);
    setViewWeekStart(weekStart);
    const newMonth = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
    setViewMonth(newMonth);
    onDateRangeChange?.(getWeekRange(weekStart));
  };

  const isToday = (dayItem) => {
    if (!dayItem) return false;
    const today = new Date();
    return (
      dayItem.date.getDate() === today.getDate() &&
      dayItem.date.getMonth() === today.getMonth() &&
      dayItem.date.getFullYear() === today.getFullYear()
    );
  };

  const hasFlights = (dayItem) => {
    if (!dayItem) return false;
    if (dayItem.month === "current") {
      return datesWithFlights.has(dayItem.day);
    }
    return dateHasFlights(flights, dayItem.date);
  };

  const monthYearDisplay = useMemo(() => {
    const displayDate = viewMode === "week" ? viewWeekStart : viewMonth;
    return displayDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }, [viewMode, viewWeekStart, viewMonth]);

  const weekInfo = useMemo(() => {
    if (viewMode === "week") {
      return `Week ${currentWeekNumber}`;
    }
    
    const weekRange = getMonthWeekRange(viewMonth);
    return formatWeekRange(weekRange.firstWeek, weekRange.lastWeek);
  }, [viewMode, currentWeekNumber, viewMonth]);

  return (
    <>
      <CalendarHeader
        monthYearDisplay={monthYearDisplay}
        weekInfo={weekInfo}
        viewMode={viewMode}
        onViewToggle={handleViewToggle}
      />

      <Card className="overflow-hidden !rounded-3xl">
        <CardContent className="p-4 !px-2">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, index) => (
              <div
                key={index}
                className="text-center text-xs font-medium text-gray-500 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="space-y-1">
            {calendarRows.map((row, rowIndex) => {
              const isRowVisible =
                viewMode === "month" || rowIndex === visibleWeekRowIndex;

              return (
                <div
                  key={`row-${rowIndex}`}
                  className={`
                    grid grid-cols-7 gap-1
                    transition-all duration-500 ease-in-out
                    ${
                      isRowVisible
                        ? "max-h-20 opacity-100"
                        : "max-h-0 opacity-0 overflow-hidden"
                    }
                  `}
                >
                  {row.map((dayItem, dayIndex) => {
                    if (!dayItem) {
                      return (
                        <div
                          key={`empty-${dayIndex}`}
                          className="aspect-square"
                        />
                      );
                    }

                    const isCurrentDate = isToday(dayItem);
                    const hasFlight = hasFlights(dayItem);
                    const isOtherMonth = dayItem.month !== "current";
                    const isDateSelected = selectedDate &&
                      dayItem.date.getDate() === selectedDate.getDate() &&
                      dayItem.date.getMonth() === selectedDate.getMonth() &&
                      dayItem.date.getFullYear() === selectedDate.getFullYear();

                    return (
                      <button
                        key={`${dayItem.month}-${dayItem.day}-${rowIndex}-${dayIndex}`}
                        onClick={() => handleDateClick(dayItem)}
                        className={`
                          aspect-square flex flex-col items-center justify-center rounded-lg text-sm font-medium transition-colors
                          ${
                            isDateSelected
                              ? "bg-primary-500 text-white hover:bg-primary-600"
                              : isCurrentDate
                              ? "ring-2 ring-primary-500 text-primary-600 hover:bg-primary-50"
                              : isOtherMonth
                              ? "text-gray-400 hover:bg-gray-50"
                              : "text-gray-900 hover:bg-gray-100"
                          }
                        `}
                      >
                        <span>{dayItem.day}</span>
                        {hasFlight && (
                          <span className="w-1 h-1 rounded-full bg-blue-500 mt-0.5" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {viewMode === "month" ? (
            <MonthNavigation
              currentMonth={viewMonth}
              onMonthSelect={handleMonthSelect}
            />
          ) : (
            <WeekNavigation
              currentWeekStart={viewWeekStart}
              onWeekSelect={handleWeekSelect}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CalendarView;
